import React, { useState, useEffect } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import AlertPopup from '../../components/react/alert_popup/alert_popup';
import { GENERIC_ERROR_MESSAGE } from '../../components/react/alert_popup/constants';
import { createQueryString } from '../../utilities/utilities';
import { Job } from '../../job_search/types';
import HeaderSection from '../../components/react/header_section/header_section';
import JobItem from '../../components/react/job_item/job_item';
import NoJobs from './no_jobs/no_jobs';
import JobsCaBanner from './jobs_ca_banner/jobs_ca_banner';
import { Props } from './types';
import { getExclusiveJobs, getAllJobs } from './services/organization_jobs.service';
import { JOBS_SEARCH_PATH } from '../../utilities/devex_paths';
import styles from './organization_jobs.module.scss';

const returnToValue = window.location.pathname;

const OrganizationJobs = ({
  orgName,
  isSignedIn,
  viewExclusiveJobs,
  membership,
  isAdmin,
}: Props) => {
  const [alertData, setAlertData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [featuredJobs, setFeaturedJobs] = useState<Job[]>([]);
  const [nonFeaturedJobs, setNonFeaturedJobs] = useState<Job[]>([]);
  const [displayedJobs, setDisplayedJobs] = useState<Job[]>([]);
  const [totalJobs, setTotalJobs] = useState<number>(0);
  const [exclusiveJobs, setExclusiveJobs] = useState<number>(0);

  useEffect(() => {
    setLoading(true);

    Promise.all([
      getAllJobs(orgName, isSignedIn, membership),
      getExclusiveJobs(orgName, isSignedIn, membership),
    ])
      .then(([allJobsResponse, exclusiveJobsResponse]) => {
        const [jobsResponse, featuredJobsResponse] = allJobsResponse;
        const slicedFeaturedJobs = featuredJobsResponse.data.slice(0, 6);
        const slicedJobs = jobsResponse.data.slice(0, 6 - slicedFeaturedJobs.length);

        unstable_batchedUpdates(() => {
          setFeaturedJobs(slicedFeaturedJobs);
          setNonFeaturedJobs(slicedJobs);
          setTotalJobs(jobsResponse.total + featuredJobsResponse.total);
          setExclusiveJobs(exclusiveJobsResponse);
        });
      })
      .catch(() => setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (featuredJobs.length || nonFeaturedJobs.length) {
      setDisplayedJobs([...featuredJobs, ...nonFeaturedJobs]);
    }
  }, [featuredJobs, nonFeaturedJobs]);

  const displayCABanner = !loading && exclusiveJobs > 0 && !viewExclusiveJobs;

  const loader = (
    <div className='d-flex align-items-center justify-content-center mt-45'>
      <div className='spinner-border text-primary' role='status'>
        <span className='visually-hidden'>Loading...</span>
      </div>
    </div>
  );

  const AddJobButton = isAdmin && (
    <div>
      <a className={`${styles.addNewJob} btn btn-info`} href='/jobs/new' data-testid='add-new-job'>
        <i className='fal icon-plus me-2' />
        Add new job
      </a>
    </div>
  );

  const jobList = displayedJobs.length >= 1 && (
    <div className={styles.jobListContainer} data-testid='job-list-container'>
      {displayedJobs.map((job) => (
        <div key={job.id} data-testid='job-container'>
          <JobItem
            job={job}
            userInfo={{ isSignedIn, viewExclusiveJobs }}
            returnToValue={returnToValue}
            featuredJobs={featuredJobs}
            setFeaturedJobs={setFeaturedJobs}
            nonFeaturedJobs={nonFeaturedJobs}
            setNonFeaturedJobs={setNonFeaturedJobs}
          />
        </div>
      ))}
    </div>
  );

  const discoverJobsLink =
    JOBS_SEARCH_PATH +
    createQueryString({
      filter: { organization_names: [orgName] },
    });

  const discoverJobs = (
    <div>
      <a
        className='btn btn-link link-blue-orange'
        href={discoverJobsLink}
        data-testid='discover-jobs'
      >
        Discover all their open positions
        <i className='fal icon-arrow-right ms-2 text-primary' />
      </a>
    </div>
  );

  return (
    <>
      <AlertPopup alertData={alertData} setAlertData={setAlertData} />
      <div className={styles.organizationJobs} data-testid='organization-jobs'>
        <HeaderSection
          title='Latest Jobs'
          total={totalJobs}
          linkText={`open position${totalJobs === 1 ? '' : 's'}`}
          linkValue={discoverJobsLink}
          secondaryText='currently in this organization'
        />
        {loading && loader}
        {AddJobButton}
        {!loading && displayedJobs.length < 1 && <NoJobs isAdmin={isAdmin} />}
        {jobList}
        {!loading && totalJobs > 0 && !displayCABanner && discoverJobs}
        {displayCABanner && <JobsCaBanner exclusiveJobs={exclusiveJobs} isSignedIn={isSignedIn} />}
      </div>
    </>
  );
};

export default OrganizationJobs;
